import React, { useEffect, useState } from "react";
import { IGroup, IProduct, ISize, IStore } from "../../../../classes/interfaces";
import DesktopHeader from "./DesktopHeader";
import DesktopHero from "./DesktopHero";
import DesktopRail from "./DesktopRail";
import DesktopProductCard from "./DesktopProductCard";
import DesktopSizeCard from "./DesktopSizeCard";
import DesktopCartSidebar from "./DesktopCartSidebar";
import Icon from "../../../../components/ui/Icon";
import { resolveGroupIcon } from "../../../../helpers/groupIcon.helper";
import {
  Root,
  Content,
  ProductColumn,
  Section,
  SectionHeader,
  SectionIconBadge,
  SectionTitle,
  Grid,
  EmptySearch,
} from "./DesktopShell.styles";

interface DesktopShellProps {
  company: IStore;
  tableId?: string;
  groups: IGroup[];
  /** Catálogo completo do canal (sem o filtro da busca): o hero descreve a
   *  loja, então sua tagline não deve encolher enquanto o cliente digita. */
  allGroups?: IGroup[];
  query: string;
  onQueryChange: (query: string) => void;
  qtyOf: (productId: string) => number;
  cartCount: number;
  isMenuStyle?: boolean;
  onOpenProduct: (product: IProduct) => void;
  onOpenSize: (size: ISize) => void;
  onOpenTaxas: () => void;
  onCart: () => void;
  onOrders?: () => void;
  onLogo?: () => void;
}

export default function DesktopShell({
  company,
  tableId,
  groups,
  allGroups,
  query,
  onQueryChange,
  qtyOf,
  cartCount,
  isMenuStyle = false,
  onOpenProduct,
  onOpenSize,
  onOpenTaxas,
  onCart,
  onOrders,
  onLogo,
}: DesktopShellProps) {
  const [activeId, setActiveId] = useState<string | null>(groups[0]?.id ?? null);

  useEffect(() => {
    function handleScroll() {
      let current: string | null = null;
      groups.forEach((group) => {
        const el = document.getElementById(`group${group.id}`);
        if (el && window.pageYOffset >= el.offsetTop - 140) {
          current = group.id;
        }
      });
      if (current && current !== activeId) {
        setActiveId(current);
      }
    }

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [groups, activeId]);

  function scrollToGroup(groupId: string) {
    setActiveId(groupId);
    const el = document.getElementById(`group${groupId}`);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }

  const hasResults = groups.some((g) => g.products.length > 0 || (g.sizes && g.sizes.length > 0));

  return (
    <Root>
      <DesktopHeader
        company={company}
        tableId={tableId}
        count={cartCount}
        query={query}
        onQueryChange={onQueryChange}
        onCart={isMenuStyle ? undefined : onCart}
        onOrders={onOrders}
        onLogo={onLogo}
      />
      <DesktopHero
        company={company}
        groups={allGroups ?? groups}
        tableId={tableId}
        onOpenTaxas={onOpenTaxas}
        isMenu={isMenuStyle}
      />
      <DesktopRail groups={groups} activeId={activeId} onPick={scrollToGroup} />

      <Content>
        <ProductColumn>
          {hasResults ? (
            groups.map((group) => (
              <Section id={`group${group.id}`} key={group.id}>
                <SectionHeader>
                  <SectionIconBadge>
                    <Icon name={resolveGroupIcon(group)} size={19} />
                  </SectionIconBadge>
                  <SectionTitle>{group.name}</SectionTitle>
                </SectionHeader>
                <Grid>
                  {group.products.length > 0
                    ? group.products.map((product) => (
                        <DesktopProductCard
                          key={product.id}
                          product={product}
                          qty={qtyOf(product.id)}
                          onOpen={onOpenProduct}
                          readOnly={isMenuStyle}
                        />
                      ))
                    : group.sizes?.map((size) => (
                        <DesktopSizeCard
                          key={size.id}
                          size={size}
                          onOpen={() => onOpenSize({ ...size, groupId: group.id, groupName: group.name })}
                        />
                      ))}
                </Grid>
              </Section>
            ))
          ) : (
            <EmptySearch>Nenhum produto encontrado para essa busca.</EmptySearch>
          )}
        </ProductColumn>

        {/* modo /menu/:suffix é vitrine sem checkout — mesma regra do CartFooter mobile */}
        {!isMenuStyle && <DesktopCartSidebar company={company} onCheckout={onCart} />}
      </Content>
    </Root>
  );
}
